import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, CardContent } from '@mui/material';
import { AuthenticatedTemplate } from "@azure/msal-react";
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { FeeScheduleEditHeader, FeeScheduleOverview, FeeScheduleTabStrip } from '../components/sections';
import { useAppSelector, useAppDispatch } from '../../../app/store';
import { useGetFeeScheduleQuery, useGetContractQuery } from '../../../services/ContractService';
import { setIsEditing, setSelectedFeeSchedule } from '../../../services/ContractService/contractSlice';
import { selectCurrentFeeSchedule, selectIsDirty } from '../../../services/ContractService/contractSlice/selectors';
import { PageLoading as Loading } from '../../common/components';

// Component for handling missing parameters
const MissingParameterError: React.FC = () => (
  <PageContainer>
    <Box display="flex" justifyContent="center">
      <PageCard>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Missing required parameters
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Both company and fee schedule ID are required to edit a fee schedule.
          </Typography>
        </CardContent>
      </PageCard>
    </Box>
  </PageContainer>
);

// Component for loading state
const LoadingState: React.FC = () => <Loading />;

// Component for error state
const ErrorState: React.FC = () => (
  <PageContainer>
    <Box display="flex" justifyContent="center">
      <PageCard>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Error loading fee schedule
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please try again or go back to the contract list.
          </Typography>
        </CardContent>
      </PageCard>
    </Box>
  </PageContainer>
);

// Component for fee schedule not found
const FeeScheduleNotFound: React.FC = () => (
  <PageContainer>
    <Box display="flex" justifyContent="center">
      <PageCard>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Fee schedule not found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The requested fee schedule could not be found.
          </Typography>
        </CardContent>
      </PageCard>
    </Box>
  </PageContainer>
);

const FeeScheduleEdit: React.FC = () => {
  // Hot reload test - testing sessionStorage backup/restore functionality
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const company = searchParams.get('company');
  const feeScheduleId = searchParams.get('feeScheduleId');
  
  const currentFeeSchedule = useAppSelector(selectCurrentFeeSchedule);
  const isDirty = useAppSelector(selectIsDirty);

  // Query to fetch the fee schedule
  const { error, isLoading, refetch } = useGetFeeScheduleQuery(
    { id: feeScheduleId! },
    { 
      skip: !feeScheduleId,
      refetchOnMountOrArgChange: true  // Always refetch when component mounts
    }
  );

  // Query to fetch the associated contract
  const { data: contract } = useGetContractQuery(
    { id: currentFeeSchedule?.contractId!, company: company! },
    { skip: !currentFeeSchedule?.contractId || !company }
  );

  useEffect(() => {
    // Only clear the fee schedule, not the contract (so back navigation preserves contract state)
    dispatch(setSelectedFeeSchedule(null));
  }, [dispatch]);

  useEffect(() => {
    // Force refetch on hot reload by calling refetch when component mounts
    if (feeScheduleId) {
      refetch();
    }
  }, [feeScheduleId, refetch]);

  useEffect(() => {
    // Set editing mode when fee schedule is loaded
    if (currentFeeSchedule) {
      dispatch(setIsEditing(true));
    }
  }, [currentFeeSchedule, dispatch]);

  useEffect(() => {
    // Redirect if required params are missing
    if (!company || !feeScheduleId) {
      navigate('/contracts');
      return;
    }
  }, [company, feeScheduleId, navigate]);

  // Handle missing parameters
  if (!company || !feeScheduleId) {
    return (
      <AuthenticatedTemplate>
        <MissingParameterError />
      </AuthenticatedTemplate>
    );
  }

  // Handle loading state
  if (isLoading) {
    return (
      <AuthenticatedTemplate>
        <LoadingState />
      </AuthenticatedTemplate>
    );
  }

  // Handle error state
  if (error) {
    return (
      <AuthenticatedTemplate>
        <ErrorState />
      </AuthenticatedTemplate>
    );
  }

  // Handle fee schedule not found
  if (!currentFeeSchedule) {
    return (
      <AuthenticatedTemplate>
        <FeeScheduleNotFound />
      </AuthenticatedTemplate>
    );
  }

  return (
    <AuthenticatedTemplate>
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard>
            <FeeScheduleEditHeader 
              company={company} 
              isDirty={isDirty} 
            />
            
            <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
              <Stack spacing={3}>
                <FeeScheduleOverview contract={contract} />
                <FeeScheduleTabStrip />
              </Stack>
            </CardContent>
          </PageCard>
        </Box>
      </PageContainer>
    </AuthenticatedTemplate>
  );
};

export default FeeScheduleEdit;
